'use client';

import type { UrlObject } from 'node:url';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import clsx from 'clsx';
import type { Route } from 'next';
import styles from './ActionButton.module.scss';

type ActionButtonSize =
  | 'xSmall'
  | 'small'
  | 'default'
  | 'large'
  | 'spCompact'
  | 'xLarge'
  | 'xxLarge'
  | 'fullWidth'
  | 'fullWidthLarge'
  | 'fullWidthMedium'
  | 'fullWidthSmall'
  | 'fullWidthCompact';

const capitalize = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

function ActionButton<T extends string>({
  children,
  onClick,
  href,
  type = 'button',
  disabled = false,
  size = 'default',
  sizeSp,
  color = 'black',
  bgColor = 'white',
  outlined = false,
  borderColor,
  fontWeight = 'bold',
  isLoading = false,
  dataTestId,
  dataPhAction,
  isShadow = false,
  noDisabledOpacity = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  href?: Route<T> | UrlObject;
  type?: 'button' | 'submit';
  disabled?: boolean;
  /** PC（@include media-min(pc)）でのサイズ */
  size?: ActionButtonSize;
  /** SPでのサイズ。未指定の場合は size と同じ（SP/PC同一サイズ） */
  sizeSp?: ActionButtonSize;
  /** 参照: src/styles/partials/_variables.scss */
  color?: string;
  bgColor?: string;
  outlined?: boolean;
  /** 参照: src/styles/partials/_variables.scss - outlinedがtrueの場合の枠線の色（未指定の場合はbgColorを使用） */
  borderColor?: string;
  /** ラベルの太さ。主導線でないボタン（サブ導線）を目立たせすぎたくない場合に 'normal' を渡す */
  fontWeight?: 'normal' | 'bold';
  isLoading?: boolean;
  dataTestId?: string;
  /** PostHog Autocapture 用の安定フック。`data-ph-capture-attribute-design-action` として描画する（GF-2121） */
  dataPhAction?: string;
  isShadow?: boolean;
  /**
   * disabled時のopacity: 0.3を適用しない。
   * デフォルトのdisabled表現はopacity: 0.3だが、Figmaデザインで背景色だけ変えて
   * 文字の透明度は変えたくないケースがある。その場合にtrueにして、
   * 呼び出し側のbgColor/color propsで非活性スタイルを制御する。
   */
  noDisabledOpacity?: boolean;
}) {
  const isClickable = !disabled && !isLoading;
  // size は PC（media-min(pc)）、sizeSp は SP のサイズを制御する。sizeSp 未指定なら size と同じ
  const spSize = sizeSp ?? size;
  const sizeClassName = clsx(styles[`pc${capitalize(size)}`], styles[`sp${capitalize(spSize)}`]);

  const handleClick = () => {
    if (isLoading) {
      return;
    }

    onClick?.();
  };

  return (
    <>
      {href ? (
        <LoadingLink
          href={href}
          className={clsx(styles.actionButton, sizeClassName, {
            [styles.disableOpacity]: !isClickable && !noDisabledOpacity,
            [styles.shadow]: isShadow,
          })}
          style={generateStyle({ color, bgColor, outlined, borderColor, fontWeight })}
          data-testid={dataTestId}
        >
          {children}
        </LoadingLink>
      ) : (
        <button
          className={clsx(styles.actionButton, sizeClassName, {
            [styles.disableOpacity]: !isClickable && !noDisabledOpacity,
            [styles.shadow]: isShadow,
          })}
          type={type}
          onClick={handleClick}
          style={generateStyle({ color, bgColor, outlined, borderColor, fontWeight })}
          disabled={!isClickable}
          data-testid={dataTestId}
          data-ph-capture-attribute-design-action={dataPhAction}
        >
          {isLoading ? <div className={styles.loader} /> : children}
        </button>
      )}
    </>
  );
}

const generateStyle = ({
  color,
  bgColor,
  outlined,
  borderColor,
  fontWeight,
}: {
  color?: string;
  bgColor?: string;
  outlined?: boolean;
  borderColor?: string;
  fontWeight?: 'normal' | 'bold';
}): React.CSSProperties => {
  if (outlined) {
    const borderColorValue = borderColor ?? bgColor;
    return {
      color: `var(--color-${color})`,
      border: `1px solid var(--color-${borderColorValue})`,
      background: `var(--color-${bgColor})`,
      fontWeight,
    };
  }
  return {
    color: `var(--color-${color})`,
    background: `var(--color-${bgColor})`,
    fontWeight,
  };
};

export default ActionButton;
